import { Modal } from "antd";
import dayjs from "dayjs";

/**
 * Kod 409 z products-service: klientka ma już opłacone inne zamówienie tej
 * samej ceny sprzed chwili (TRE-2055). Dwa przejścia checkoutu manualnego
 * zakładają dwie subskrypcje Stripe — potwierdzenie drugiego zamówienia nie
 * doksięgowuje raty, tylko przepina klientkę na drugą subskrypcję i zostawia
 * pierwszą żywą.
 */
export const DUPLICATE_PURCHASE_ERROR = "duplicate_purchase";

export interface DuplicatePurchaseRejection {
  message: string;
  paidOrderNumber: number | null;
  /** ISO — kiedy klientka złożyła zamówienie, które jest już opłacone. */
  paidOrderCreatedAt: string | null;
}

/** Odmowa duplikatu zakupu, czy zwykły błąd? `null` = zwykły, obsłuż jak dotąd. */
export const readDuplicatePurchase = (error: unknown): DuplicatePurchaseRejection | null => {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: {
          code?: unknown;
          message?: unknown;
          data?: { paidOrderNumber?: unknown; paidOrderCreatedAt?: unknown };
        };
      };
    }
  )?.response;

  if (response?.status !== 409 || response.data?.code !== DUPLICATE_PURCHASE_ERROR) {
    return null;
  }

  const { paidOrderNumber, paidOrderCreatedAt } = response.data?.data ?? {};

  return {
    message: typeof response.data.message === "string" ? response.data.message : "",
    paidOrderNumber: typeof paidOrderNumber === "number" ? paidOrderNumber : null,
    paidOrderCreatedAt: typeof paidOrderCreatedAt === "string" ? paidOrderCreatedAt : null,
  };
};

/**
 * Rzucane, gdy operator wybrał „Nie potwierdzaj". Nic się nie stało i nie ma
 * o czym krzyczeć — hook rozpoznaje ten typ i pomija czerwony toast.
 */
export class DuplicatePurchaseDismissed extends Error {
  constructor() {
    super("Potwierdzenie przerwane przez operatora");
    this.name = "DuplicatePurchaseDismissed";
  }
}

export const isDuplicatePurchaseDismissed = (error: unknown): boolean => error instanceof DuplicatePurchaseDismissed;

/**
 * Pyta, czy naprawdę potwierdzić zamówienie, które wygląda na duplikat.
 *
 * Domyślnym przyciskiem jest „Nie potwierdzaj": ta ścieżka istnieje, bo
 * klientka przeszła koszyk dwa razy, więc odruchowy Enter ma cofać.
 */
export const askAboutDuplicatePurchase = ({
  message,
  paidOrderNumber,
  paidOrderCreatedAt,
}: DuplicatePurchaseRejection): Promise<boolean> =>
  new Promise((resolve) => {
    const paidOrder = paidOrderNumber ? `nr ${paidOrderNumber}` : "";
    const paidAt = paidOrderCreatedAt ? ` z ${dayjs(paidOrderCreatedAt).format("DD.MM.YYYY HH:mm")}` : "";

    Modal.confirm({
      title: "To zamówienie wygląda na duplikat",
      content:
        message ||
        `Klientka ma już opłacone zamówienie ${paidOrder}${paidAt} na tę samą cenę. Potwierdzenie założy jej drugą subskrypcję.`,
      okText: "Potwierdź mimo to",
      okButtonProps: { danger: true },
      cancelText: "Nie potwierdzaj",
      autoFocusButton: "cancel",
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
